import { z } from 'zod';

import { ArticleSchema } from './article';
import { PageSchema } from './page';
import { SiteSchema } from './site';
import { StrategySchema } from './strategy';
import { TeamMemberSchema } from './team-member';
import { TierSchema } from './tier';

export * from './primitives';
export * from './site';
export * from './strategy';
export * from './article';
export * from './tier';
export * from './team-member';
export * from './page';

const KNOWN_ROUTES = new Set<string>([
  '/',
  '/strategies',
  '/performance',
  '/research',
  '/pricing',
  '/about',
  '/docs',
  '/login',
  '/signup',
  '/dashboard',
]);

const REQUIRED_TIERS = ['starter', 'pro', 'institutional'] as const;

export const ContentSchema = z
  .object({
    site: SiteSchema,
    strategies: z.array(StrategySchema),
    articles: z.array(ArticleSchema),
    tiers: z.array(TierSchema),
    team: z.array(TeamMemberSchema),
    pages: z.record(z.string(), PageSchema),
  })
  .superRefine((c, ctx) => {
    const checkUnique = <T,>(arr: T[], key: (x: T) => string, label: string) => {
      const map = new Map<string, number>();
      arr.forEach((x, i) => {
        const k = key(x);
        if (map.has(k)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [label, i, 'id'],
            message: `Duplicate ${label} id: ${k}`,
          });
        }
        map.set(k, i);
      });
    };
    checkUnique(c.strategies, (s) => s.id, 'strategies');
    checkUnique(c.articles, (a) => a.id, 'articles');
    checkUnique(c.team, (t) => t.id, 'team');
    checkUnique(c.tiers, (t) => t.id, 'tiers');

    const heroes = c.articles.filter((a) => a.hero);
    if (heroes.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['articles'],
        message: `Expected exactly 1 hero article, got ${heroes.length}`,
      });
    }

    const tierIds = new Set(c.tiers.map((t) => t.id));
    for (const required of REQUIRED_TIERS) {
      if (!tierIds.has(required)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['tiers'],
          message: `Missing required tier: ${required}`,
        });
      }
    }

    for (const [i, s] of c.strategies.entries()) {
      if (!tierIds.has(s.tier)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['strategies', i, 'tier'],
          message: `Strategy ${s.id} references unknown tier ${s.tier}`,
        });
      }
    }

    for (const [i, link] of c.site.nav.entries()) {
      if (!KNOWN_ROUTES.has(link.href) && !link.href.startsWith('/research/')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['site', 'nav', i, 'href'],
          message: `Nav href ${link.href} is not a known route`,
        });
      }
    }
  });

export type Content = z.infer<typeof ContentSchema>;
